import React, { useEffect, useState, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { jwtDecode } from 'jwt-decode';

const AccountPage = () => {
  const { keycloak } = useKeycloak();
  const token = keycloak.token;
  const avatarInputRef = useRef();
  const coverInputRef = useRef();

  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    display_name: '',
    handle: '',
    avatar_url: '',
    cover_url: '',
    bio: ''
  });
  const [editMode, setEditMode] = useState(false);
  const [media, setMedia] = useState([]);
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (!token) return;

    try {
      const decoded = jwtDecode(token);
      const realmRoles = decoded?.realm_access?.roles || [];
      const groupList = decoded?.groups || [];
      setRoles(realmRoles);
      setGroups(groupList);
    } catch (err) {
      console.error('Erreur lors du décodage du token:', err);
    }

    fetch("https://www.weavevo.com/myProfil", {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        setProfile(data);
        setForm({
          display_name: data.display_name || '',
          handle: data.handle || '',
          avatar_url: data.avatar_url || '',
          cover_url: data.cover_url || '',
          bio: data.bio || ''
        });
      });
  }, [token]);

  useEffect(() => {
    if (!profile?.username) return;

    fetch("https://www.weavevo.com/media/me", {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        const myMedia = data.filter(m => m.contributor === profile.username);
        setMedia(myMedia);
      });
  }, [profile]);

  const handleSave = () => {
    fetch("https://www.weavevo.com/myProfil", {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(form)
    })
      .then(res => res.json())
      .then(data => {
        alert("Profil mis à jour !");
        setEditMode(false);
        setProfile(prev => ({ ...prev, ...form }));
      });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const data = new FormData();
    data.append('avatar', file);
    fetch("https://www.weavevo.com/me/avatar", {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
      body: data
    })
      .then(res => res.json())
      .then(data => setForm(prev => ({ ...prev, avatar_url: data.url })));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const data = new FormData();
    data.append('cover', file);
    fetch("https://www.weavevo.com/me/cover", {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
      body: data
    })
      .then(res => res.json())
      .then(data => setForm(prev => ({ ...prev, cover_url: data.url })));
  };

  if (!profile) return <div className="p-5">Chargement...</div>;

  return (
    <div className="container py-4" >

      {/* Couverture */}
      <div className="mb-4 position-relative rounded-4 overflow-hidden" style={{ paddingTop: '0px',height: '300px', backgroundColor: '#eee' }}>
        {form.cover_url && (
          <img src={`https://www.weavevo.com${form.cover_url}`} alt="cover" className="w-100 h-100 object-fit-cover" />
        )}
        <button
          className="btn btn-sm btn-dark position-absolute bottom-0 end-0 m-2"
          onClick={() => coverInputRef.current.click()}
        >
          Modifier la couverture
        </button>
        <input type="file" accept="image/*" hidden ref={coverInputRef} onChange={handleCoverChange} />
      </div>

      {/* Profil utilisateur */}
      <div className="card shadow-sm rounded-4 p-4 mb-5">
        <div className="d-flex align-items-center mb-3">
          <div onClick={() => avatarInputRef.current.click()} style={{ cursor: 'pointer' }}>
            <img
              src={form.avatar_url ? `https://www.weavevo.com${form.avatar_url}` : "https://via.placeholder.com/80?text=?"}
              alt="avatar"
              className="rounded-circle me-3 border"
              style={{ width: 80, height: 80, objectFit: 'cover' }}
            />
          </div>
          <input type="file" accept="image/*" hidden ref={avatarInputRef} onChange={handleAvatarChange} />
          <div>
            {editMode ? (
              <>
                <input className="form-control mb-2" value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} />
                <input className="form-control mb-2" value={form.handle} onChange={e => setForm({ ...form, handle: e.target.value })} />
              </>
            ) : (
              <>
                <h5 className="mb-0 fw-bold">{profile.display_name}</h5>
                <p className="text-muted mb-1">{profile.handle}</p>
              </>
            )}
          </div>
        </div>

        <div className="mb-3">
          {editMode ? (
            <textarea className="form-control" rows="3" value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} />
          ) : (
            <p className="text-muted">{profile.bio}</p>
          )}
        </div>

        <div className="text-end">
          {editMode ? (
            <>
              <button className="btn btn-secondary me-2" onClick={() => setEditMode(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave}>💾 Enregistrer</button>
            </>
          ) : (
            <button className="btn btn-outline-primary" onClick={() => setEditMode(true)}>✏️ Modifier</button>
          )}
        </div>
      </div>

      {/* Groupes Keycloak */}
      <div className="card shadow-sm rounded-4 p-4 mb-5">
        <h5 className="mb-3">👥 Groupes </h5>
        {groups.length > 0 ? (
          <ul className="list-unstyled mb-0">
            {groups.map((group, index) => (
              <li key={index} className="badge border text-dark me-2 mb-2" style={{ backgroundColor: '#f8f9fa', fontWeight: 500 }}>{group}</li>
            ))}
          </ul>
        ) : (
          <p className="text-muted">Aucun groupe attribué.</p>
        )}
      </div>

      {/* Contributions */}
      <section>
        <h4 className="mb-4">📷 Mes contributions</h4>
        {media.length > 0 ? (
          <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 g-4">
            {media.map(m => (
              <div className="col" key={m.id}>
                <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden media-card">
                  <div className="media-wrapper">
                    {m.file_path.match(/\.(jpg|jpeg|png)$/i) ? (
                      <img src={`https://www.weavevo.com/uploads/${m.file_path}`} className="img-fluid" alt="media" />
                    ) : (
                      <video src={`https://www.weavevo.com/uploads/${m.file_path}`} className="img-fluid" controls />
                    )}
                  </div>
                  <div className="p-3">
                    <h6 className="fw-semibold mb-1 text-truncate">{m.title}</h6>
                    <p className="text-muted small mb-0">{m.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted">Aucune contribution trouvée.</p>
        )}
      </section>
    </div>
  );
};

export default AccountPage;